import React, { useRef, useState } from 'react'
import Button, { buttonClasses } from '@mui/material/Button'
import ButtonGroup, { buttonGroupClasses } from '@mui/material/ButtonGroup'
import CircularProgress from '@mui/material/CircularProgress'
import Popper from '@mui/material/Popper'
import Paper from '@mui/material/Paper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MenuList from '@mui/material/MenuList'
import MenuItem from '@mui/material/MenuItem'
import ListSubheader from '@mui/material/ListSubheader'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import CheckIcon from '@mui/icons-material/Check'
import { useTranslation } from 'react-i18next'
import { filterMenuPaperSx } from '../Pages/Category/components/filterMenuStyles'

export const SHOW_MORE_OPTIONS = [10, 20, 50, 100]

// Same height as the category filter-bar buttons, which filterMenuPaperSx's top padding is sized for.
const BTN_HEIGHT = 42
const BORDER_RADIUS = '5px'

interface Props {
  shown: number
  total: number
  // How many items each "Show More" click appends.
  step: number
  loading?: boolean

  setStep(step: number): void

  onShowMore(): void
}

const ShowMoreButton: React.FC<Props> = ({ shown, total, step, setStep, loading, onShowMore }) => {
  const { t } = useTranslation()
  const anchorRef = useRef<HTMLDivElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const nextCount = Math.min(step, total - shown)

  const onSelectStep = (val: number) => {
    setMenuOpen(false)
    setStep(val)
  }

  return (
    <div className='flex flex-col items-center gap-10'>
      <div className='flex flex-col items-center gap-[6px]'>
        <span className='text-13 text-gray-700 leading-18'>
          {t('showingOfTotal', { defaultValue: 'Showing {{shown}} of {{total}}', shown, total })}
        </span>
        <div className='w-160 h-[3px] rounded-full bg-gray-300 overflow-hidden'>
          <div
            className='h-full bg-green-light transition-[width] duration-300'
            style={{ width: `${(shown / total) * 100}%` }}
          />
        </div>
      </div>
      <ButtonGroup
        ref={anchorRef}
        variant='outlined'
        color='secondary'
        disableElevation
        // Above the menu Popper, which slides under the buttons so its border wraps them (like SortByButton).
        sx={{
          position: 'relative',
          zIndex: 21,
          [`& .${buttonGroupClasses.grouped}`]: {
            height: `${BTN_HEIGHT}px`,
            borderColor: '#a3a3a3',
            borderRadius: BORDER_RADIUS,
            backgroundColor: '#fff',
            '&:hover': { borderColor: '#005058', backgroundColor: '#fff' },
            [`&.${buttonClasses.disabled}`]: { borderColor: '#a3a3a3' },
          },
          // The rounding above would otherwise also round the corners where the two buttons meet.
          [`& .${buttonGroupClasses.firstButton}`]: { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
          [`& .${buttonGroupClasses.lastButton}`]: { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
        }}
      >
        <Button
          onClick={onShowMore}
          disabled={loading}
          sx={{ minWidth: '150px', paddingLeft: '20px' }}
          startIcon={loading ? <CircularProgress size={16} color='inherit' /> : undefined}
        >
          <span className='normal-case font-bold text-13'>
            {t('button.showMoreWithCount', { defaultValue: 'Show {{count}} more', count: nextCount })}
          </span>
        </Button>
        <Button
          onClick={() => setMenuOpen(prev => !prev)}
          disabled={loading}
          sx={{ paddingRight: '14px' }}
          aria-label={t('button.itemsPerClick', 'Items per click')}
          title={t('button.itemsPerClick', 'Items per click')}
        >
          {menuOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        </Button>
      </ButtonGroup>
      <Popper
        open={menuOpen}
        anchorEl={anchorRef.current}
        placement='bottom-start'
        style={{
          borderRadius: BORDER_RADIUS,
          overflow: 'hidden',
          zIndex: 20,
          border: '1px solid #a3a3a3',
          // border-box at the buttons' exact width, so the menu's side borders continue the buttons' ones.
          boxSizing: 'border-box',
          width: anchorRef.current?.getBoundingClientRect().width,
        }}
        modifiers={[
          // Pull the menu up under the buttons so they sit inside its top padding.
          { name: 'offset', options: { offset: [0, -BTN_HEIGHT] } },
          // Flipped above, the buttons would end up at the menu's bottom instead of in its top padding.
          { name: 'flip', enabled: false },
        ]}
      >
        <Paper sx={filterMenuPaperSx}>
          <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
            <MenuList autoFocusItem={menuOpen}>
              <ListSubheader sx={{ lineHeight: '32px', fontSize: '13px' }}>
                {t('button.itemsPerClick', 'Items per click')}
              </ListSubheader>
              {SHOW_MORE_OPTIONS.map(val => (
                <MenuItem key={val} selected={step === val} onClick={() => onSelectStep(val)}>
                  <span className='flex-1'>{val}</span>
                  {step === val && <CheckIcon />}
                </MenuItem>
              ))}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </div>
  )
}

export default ShowMoreButton
